import { toast } from "sonner";
import { buildOwnerLetterHtml } from "./ownerLetterHtml";

/**
 * Render the printable owner-notice letter into a hidden iframe and print it.
 *
 * Rewritten July 2026 after intermittent "sometimes prints / sometimes
 * doesn't" reports on production. Four causes were possible; this version
 * handles all four:
 *
 *   1. Iframe DOM race — was using document.write() then reading images
 *      too early. Now uses `srcdoc` + a real `iframe.onload` event so the
 *      HTML is guaranteed parsed before we touch anything inside it.
 *   2. Image (page-2 NZTA factsheet) not loaded — we now walk *all*
 *      doc.images and Promise.all their load events with individual
 *      timeouts, so the factsheet is fully rendered before print fires.
 *   3. Browser silently blocking auto-print after an async gap — we call
 *      print inside a rAF to preserve gesture affinity where possible AND
 *      show a manual "Click to print letter" toast with a re-trigger
 *      button if the print dialog doesn't appear within 1.5s (Safari's
 *      most common failure mode — silent drop, no error).
 *   4. contentDocument null — checked explicitly with a friendly toast.
 *
 * `snap` is the letter snapshot persisted in `impound_letters`.
 * `opts.silent` suppresses success toast (used by the manual flow which
 * runs its own toast).
 */
export function printOwnerLetter(snap, opts = {}) {
  if (!snap) return;
  const { silent = false } = opts;
  const html = buildOwnerLetterHtml(snap);

  // Re-use a single hidden iframe element — remove any previous one so
  // repeat prints don't leak DOM nodes.
  const oldFrame = document.getElementById("__owner_letter_print_frame__");
  if (oldFrame) oldFrame.remove();

  const frame = document.createElement("iframe");
  frame.id = "__owner_letter_print_frame__";
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.setAttribute("aria-hidden", "true");
  // srcdoc + iframe.onload gives us a reliable "everything parsed" signal;
  // document.write did not (browsers vary on when the write commit happens).
  frame.srcdoc = html;
  document.body.appendChild(frame);

  const bail = (msg) => {
    // Never leave the user without a way to actually print.
    console.warn("[ownerLetterPrint]", msg);
    toast.error(msg, {
      duration: 10000,
      action: {
        label: "Try again",
        onClick: () => printOwnerLetter(snap, opts),
      },
    });
    try { frame.remove(); } catch { /* ignore */ }
  };

  const HARD_TIMEOUT_MS = 10000;         // Absolute guard: never hang forever.
  const AFTERCALL_WATCH_MS = 1500;       // If print dialog hasn't shown by now, prompt manual retry.
  let printed = false;
  let bailed = false;
  const hardTimer = setTimeout(() => {
    if (!printed && !bailed) { bailed = true; bail("Owner letter print timed out — click Try again to reopen the printer."); }
  }, HARD_TIMEOUT_MS);

  frame.addEventListener("load", async () => {
    const cw = frame.contentWindow;
    const doc = frame.contentDocument || cw?.document;
    if (!cw || !doc) {
      clearTimeout(hardTimer);
      if (!bailed) { bailed = true; bail("Print preview blocked — pop-ups may be disabled for this site."); }
      return;
    }

    // Wait for every image on the letter (page-2 factsheet is the critical
    // one). Individual per-image 4 s timeout so a stuck image doesn't hold
    // up the whole print.
    const images = Array.from(doc.images || []);
    console.log(`[ownerLetterPrint] iframe loaded, waiting on ${images.length} image(s)`);
    await Promise.all(images.map((img) => new Promise((resolve) => {
      if (img.complete && img.naturalHeight !== 0) return resolve();
      const done = () => resolve();
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
      setTimeout(done, 4000);
    })));

    // Small settle beat so print CSS layout is applied.
    await new Promise((r) => setTimeout(r, 60));

    // Detect "browser silently dropped print" — Safari & some strict Chrome
    // configs can drop window.print() when it fires from an async chain that
    // has broken the user-gesture context. If afterprint doesn't come back
    // within AFTERCALL_WATCH_MS, prompt the user to trigger it manually
    // (which restores a real gesture).
    let dialogShown = false;
    const onBefore = () => { dialogShown = true; };
    const onAfter = () => {
      printed = true;
      clearTimeout(hardTimer);
      // Give the browser a moment to finish the print job before we tear
      // down the iframe (Safari occasionally still holds refs post-dialog).
      setTimeout(() => { try { frame.remove(); } catch { /* ignore */ } }, 500);
      if (!silent) toast.success("Owner letter sent to printer");
    };
    cw.addEventListener("beforeprint", onBefore, { once: true });
    cw.addEventListener("afterprint", onAfter, { once: true });

    try {
      cw.focus();
      cw.print();
    } catch (err) {
      clearTimeout(hardTimer);
      if (!bailed) { bailed = true; bail(`Print call failed: ${err?.message || err}`); }
      return;
    }

    // Watchdog: after AFTERCALL_WATCH_MS, if neither beforeprint nor
    // afterprint has fired, assume the browser silently blocked. Offer a
    // manual retrigger via a toast action (button click = real gesture).
    setTimeout(() => {
      if (printed || bailed || dialogShown) return;
      console.warn("[ownerLetterPrint] beforeprint never fired — likely gesture blocked");
      toast.warning("Browser blocked the auto-print. Tap 'Print now' to open the printer.", {
        duration: 15000,
        action: {
          label: "Print now",
          onClick: () => {
            try {
              cw.focus();
              cw.print();
            } catch (e) {
              bail(`Manual retry failed: ${e?.message || e}`);
            }
          },
        },
      });
    }, AFTERCALL_WATCH_MS);
  }, { once: true });

  frame.addEventListener("error", () => {
    clearTimeout(hardTimer);
    if (!bailed) { bailed = true; bail("Print iframe failed to load — refresh the page and try again."); }
  }, { once: true });
}
